/**
 * API Key Management Component
 * Allows users to create, view, and revoke API keys
 */

import React, { useState, useEffect } from 'react';
import { Key, Plus, Trash2, Copy, Check, AlertCircle, ExternalLink } from 'lucide-react';
import { createApiKey, listApiKeys, revokeApiKey, type ApiKey, type CreateApiKeyResponse } from '@/services/apiKeyService';

const ApiKeyManagement: React.FC = () => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showNewKey, setShowNewKey] = useState<string | null>(null);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  const [copiedMcpUrl, setCopiedMcpUrl] = useState<boolean>(false);
  const [showKeyId, setShowKeyId] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [createLabel, setCreateLabel] = useState('');

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('[API Keys] Loading keys...');
      const apiKeys = await listApiKeys();
      console.log('[API Keys] Loaded keys:', apiKeys.length, apiKeys);
      setKeys(apiKeys);
      // Don't show error if we got an empty array (no keys yet or endpoint not deployed)
      // Only show error if there was an actual error message
    } catch (err: any) {
      console.error('[API Keys] Error loading API keys:', err);
      console.error('[API Keys] Error details:', {
        message: err.message,
        name: err.name,
        stack: err.stack,
      });
      // Only show error if it's not a network/endpoint issue
      // Network errors are handled in the service and return empty array
      if (err.message && !err.message.includes('Failed to fetch') && !err.message.includes('NetworkError')) {
        setError(err.message || 'Failed to load API keys');
      } else {
        // For network errors, just set empty array (service already handles this)
        setKeys([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async () => {
    // Validate label is provided
    if (!createLabel || createLabel.trim() === '') {
      setError('Label is required. Please enter a label for your API key.');
      return;
    }

    try {
      setIsCreating(true);
      setError(null);
      const response: CreateApiKeyResponse = await createApiKey({
        label: createLabel.trim(),
      });
      
      setShowNewKey(response.apiKey);
      setCreateLabel('');
      await loadKeys(); // Reload to show the new key in the list
    } catch (err: any) {
      console.error('Error creating API key:', err);
      // Show user-friendly error message
      const errorMessage = err.message || 'Failed to create API key';
      setError(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to revoke this API key? It will stop working immediately.')) {
      return;
    }

    try {
      setError(null);
      await revokeApiKey(keyId);
      await loadKeys();
    } catch (err: any) {
      console.error('Error revoking API key:', err);
      setError(err.message || 'Failed to revoke API key');
    }
  };

  const copyToClipboard = async (text: string, keyId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKeyId(keyId);
      setTimeout(() => setCopiedKeyId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getMcpUrl = (apiKey: string): string => {
    // Determine if we're in localhost or production
    const isLocal = typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    );
    
    const baseUrl = isLocal
      ? 'http://localhost:5001/lemur-476302/us-central1/mcp'
      : 'https://us-central1-lemur-476302.cloudfunctions.net/mcp';
    
    return `${baseUrl}?api_key=${encodeURIComponent(apiKey)}`;
  };

  const copyMcpUrl = async (apiKey: string) => {
    try {
      const mcpUrl = getMcpUrl(apiKey);
      await navigator.clipboard.writeText(mcpUrl);
      setCopiedMcpUrl(true);
      setTimeout(() => setCopiedMcpUrl(false), 2000);
    } catch (err) {
      console.error('Failed to copy MCP URL:', err);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* New Key Modal */}
      {showNewKey && (
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 text-yellow-800">
            <AlertCircle className="w-5 h-5" />
            <h3 className="font-semibold">API Key Created</h3>
          </div>
          <p className="text-sm text-yellow-700">
            <strong>Important:</strong> Copy this key now. You won't be able to see it again!
          </p>
          
          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
            <div className="bg-white border border-yellow-300 rounded p-3 flex items-center gap-2">
              <code className="flex-1 text-sm font-mono break-all">{showNewKey}</code>
              <button
                onClick={() => copyToClipboard(showNewKey, 'new')}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="Copy API key to clipboard"
              >
                {copiedKeyId === 'new' ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* MCP URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">MCP URL (for ChatGPT)</label>
            <div className="bg-white border border-yellow-300 rounded p-3 flex items-center gap-2">
              <code className="flex-1 text-sm font-mono break-all text-xs">{getMcpUrl(showNewKey)}</code>
              <button
                onClick={() => copyMcpUrl(showNewKey)}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap"
                title="Copy MCP URL to clipboard"
              >
                {copiedMcpUrl ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy URL
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Paste this URL directly into ChatGPT's MCP configuration
            </p>
          </div>

          <button
            onClick={() => setShowNewKey(null)}
            className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
          >
            I've copied the key
          </button>
        </div>
      )}

      {/* Create New Key */}
      <div className="card p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-100 rounded-lg">
            <Plus className="w-5 h-5 text-green-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900">Create New API Key</h3>
            <p className="text-xs text-gray-500">Generate a new key for ChatGPT or other integrations</p>
          </div>
        </div>
        <div className="flex gap-2 items-start">
          <div className="flex-1">
            <input
              type="text"
              value={createLabel}
              onChange={(e) => {
                setCreateLabel(e.target.value);
                // Clear error when user starts typing
                if (error && error.includes('Label is required')) {
                  setError(null);
                }
              }}
              placeholder="Label (e.g., 'ChatGPT key - laptop')"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">* Required</p>
          </div>
          <button
            onClick={handleCreateKey}
            disabled={isCreating}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap self-start"
            style={{ height: '42px' }}
          >
            {isCreating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Create Key
              </>
            )}
          </button>
        </div>
      </div>

      {/* API Keys List */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-700">Your API Keys</h3>
        {keys.length === 0 ? (
          <div className="card p-6 text-center text-gray-500">
            <Key className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>No API keys yet. Create one to get started.</p>
          </div>
        ) : (
          keys.map((key) => (
            <div
              key={key.id}
              className={`card p-4 ${key.revoked || !key.active ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Key className="w-4 h-4 text-gray-500" />
                    <h4 className="font-semibold text-gray-900">{key.label}</h4>
                    {key.revoked || !key.active ? (
                      <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">Revoked</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">Active</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>Created: {formatDate(key.createdAt)}</p>
                    <p>Last used: {formatDate(key.lastUsedAt)}</p>
                    {key.scopes.length > 0 && (
                      <p>Scopes: {key.scopes.join(', ')}</p>
                    )}
                  </div>
                </div>
                {!key.revoked && key.active && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleRevokeKey(key.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Revoke key"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Usage Instructions */}
      <div className="card p-4 bg-blue-50 border border-blue-200">
        <h4 className="font-semibold text-blue-900 mb-2">How to use your API key</h4>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          <li>Copy your API key (shown only once when created)</li>
          <li><strong>For ChatGPT:</strong> Use the "Copy MCP URL" button when creating a key, then paste the URL into ChatGPT's MCP configuration</li>
          <li><strong>For API calls:</strong> Use it in the Authorization header: <code className="bg-blue-100 px-1 rounded">Bearer complin_sk_...</code></li>
          <li>Or pass it directly to other MCP clients</li>
        </ol>
        <div className="mt-3 p-2 bg-blue-100 rounded text-xs text-blue-900">
          <strong>MCP URL format:</strong> <code className="bg-blue-200 px-1 rounded">https://us-central1-lemur-476302.cloudfunctions.net/mcp?api_key=YOUR_KEY</code>
        </div>
        <div className="mt-3 pt-3 border-t border-blue-200">
          <a
            href="/chatgpt-integration"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-700 hover:text-blue-900 font-medium flex items-center gap-1 underline"
          >
            ChatGPT Integration Instructions
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default ApiKeyManagement;

